import type { TFunction } from "i18next";
import type { ItemKind } from "../../../../api/content/reviewDecoders";
import { readableJsonText } from "../../../../shared/formatting/readableJson";
import { appPaths } from "../../../../shared/routing/appPaths";
import type { AppPath } from "../../../../shell/workspace";
import type { AuthorIntent, EditIntent, EditWay } from "../threadIntent";
import type { EditedItem } from "./editedItem";

/** Making or changing one item (F37, SB-10 to SB-12, SB-16): the same view for both. */
export type ItemIntent = AuthorIntent | EditIntent;

/** One stored item at its exact version, as an Edit or Ask action names it. */
export interface ItemAtVersion {
  readonly itemKind: ItemKind;
  readonly name: string;
  /** The item's `VersionRef`, `<item id>@<version>`. */
  readonly reference: string;
}

/** The way the caller asks for (F116), else the person chooses it; by hand, with what to add to a Workflow's draft; the Workflow version the item is changed in. */
export interface EditRequest {
  readonly way?: EditWay;
  readonly carry?: string;
  readonly within?: string;
}

/** Work on `item`: Open, Edit and Ask lead to this one intent (SB-16); `request` names the way, else the person chooses it. */
export function editIntent(projectId: string, item: ItemAtVersion, returnTo: AppPath | null, request: EditRequest | null = null): EditIntent {
  const intent: EditIntent = { kind: "edit", projectId, returnTo, itemKind: item.itemKind, item: item.reference, name: item.name };
  if (request === null) return intent;
  return {
    ...intent,
    ...(request.way === undefined ? {} : { way: request.way }),
    ...(request.carry === undefined ? {} : { carry: request.carry }),
    ...(request.within === undefined ? {} : { within: request.within }),
  };
}

/** The item an edit intent edits, with the Workflow version it is changed in. */
export function editedItemOfIntent(intent: EditIntent): EditedItem {
  return intent.within === undefined ? { kind: "item", reference: intent.item } : { kind: "item", reference: intent.item, within: intent.within };
}

const NEW_ITEM_FORMS: Partial<Readonly<Record<ItemKind, () => AppPath>>> = {
  step: appPaths.newStep,
  artifact: appPaths.newArtifact,
  skill: appPaths.newSkill,
  label_list: appPaths.newLabelList,
  capability_overrides: () => appPaths.newCapabilityOverrides(null),
};

/**
 * Where a new item is made by hand, if it has a form (F37: "a manual Workflow can make the same
 * item"; ADR-0031 D3's Workflow for it is not built yet). A form's own draft goes back to that
 * form. An edit by hand needs no address: the preparation shows the "Edit an item" step's view.
 */
export function newItemFormPath(intent: AuthorIntent): AppPath | null {
  if (intent.draft !== null) return intent.returnTo;
  return NEW_ITEM_FORMS[intent.itemKind]?.() ?? null;
}

/**
 * The prepared first message, ready to finish (AU-2): it names the item at its exact version,
 * so the agent reads exactly that version with `read_process_item`, or carries the form's draft.
 */
export function preparedMessage(intent: ItemIntent, t: TFunction): string {
  const kind = t(`authoring.ask.kinds.${intent.itemKind}`);
  if (intent.kind === "edit") {
    if (intent.within !== undefined) return t("authoring.ask.aboutItemWithin", { kind, name: intent.name, reference: intent.item, within: intent.within });
    return t("authoring.ask.aboutItem", { kind, name: intent.name, reference: intent.item });
  }
  if (intent.draft === null) {
    return t("authoring.ask.newItem", { kind });
  }
  return t("authoring.ask.aboutDraft", { kind, name: intent.name.trim() || "—", draft: readableJsonText(intent.draft) });
}

/** The generated title (F31). */
export function authoringTitle(intent: ItemIntent, t: TFunction): string {
  const name = intent.name.trim() || t(`authoring.ask.kinds.${intent.itemKind}`);
  return t(intent.kind === "edit" ? "threads.prepare.author.editTitle" : "threads.prepare.author.newTitle", { name });
}
